import { useRef, useState } from "react";
import type { MailboxOrder, OrderState } from "../../../shared/mailboxOrders";
import { PhoneNav, type PhoneBack } from "../../../shared/phone/PhoneNav";
import { PnRow } from "../../../shared/phone/Row";
import { ActionSheet } from "../../../shared/phone/Sheet";
import { Icon } from "../../components/ui/Icon";
import { emailOf, hideProvider, metaOf, shownState, type Connection, type ConnectionKind } from "../../lib/connectionsApi";
import { IN_APP } from "../../lib/inApp";
import { mailboxCount, priceShort } from "../../lib/mailboxOrdersApi";
import { ProductMark, type ProductRef } from "../goal/FlowHead";
import { usePaneTop, useScrolledPast } from "../more/useSubScroll";
import { BuyMailboxes } from "./BuyMailboxes";
import { lastChecked, when } from "./CxRows";
import { barBack, CheckMark, CxHead, PLACES, Spin, stateOf } from "./cxNative";
import { useMailboxOrders } from "./MailboxOrders";
import type { CxFlows } from "./useCxFlows";

/** An order's state in a word or two, the way a row's pill can hold it. */
const ORDER_STATE: Record<OrderState, { words: string; tone: string }> = {
  draft: { words: "Draft", tone: "" },
  placing: { words: "Buying", tone: "acc" },
  unsure: { words: "Checking", tone: "warn" },
  placed: { words: "On the way", tone: "acc" },
  ready: { words: "Ready", tone: "good" },
  failed: { words: "Refused", tone: "danger" },
  cancelled: { words: "Cancelled", tone: "" },
};

/** A connection's own screen: its checks first with Fix it on the one that fails, its settings, and Disconnect alone at the bottom. */
export function ConnectionDetail({
  product,
  kind,
  conn,
  f,
  say,
  onReady,
  back,
}: {
  product: ProductRef;
  kind: ConnectionKind;
  conn: Connection | undefined;
  f: CxFlows;
  say: (message: string, done?: boolean) => void;
  onReady: () => void;
  back?: PhoneBack;
}) {
  const top = useRef<HTMLDivElement>(null);
  const past = useScrolledPast(top, 110);
  usePaneTop(top, `${product.id}|${kind}`);
  const [askOff, setAskOff] = useState(false);
  const st = shownState(conn);
  const working = f.busy === kind;
  const s = stateOf(conn, working);
  const p = PLACES[kind];
  const on = st !== "none";

  const line = (
    <>
      <span className="mo-pmark">
        <ProductMark product={product} />
      </span>
      {product.name}. {p.for}.
    </>
  );

  return (
    <div className={past ? "pn-screen mo-cx-screen past" : "pn-screen mo-cx-screen"} ref={top}>
      <PhoneNav title={p.name} back={barBack(back, past, p.name)} />
      <div className="pn-stack">
        <CxHead place={kind} tone={s.tone} state={s.words} stateIcon={s.icon} line={line} on={on} />
        {!on ? (
          <>
            {kind === "meta" && metaOf(conn).lastError && <p className="mo-err mo-pad">{hideProvider(metaOf(conn).lastError ?? "")}</p>}
            {f.may && (
              <div className="pn-grp">
                <PnRow className="accent" icon={<Icon name="plug" />} title="Connect" onClick={() => f.start(kind)} chevron={false} />
              </div>
            )}
          </>
        ) : (
          <>
            <Checks kind={kind} conn={conn} f={f} />
            <Settings kind={kind} conn={conn} f={f} />
            {kind === "email" && f.may && <SendingDomains product={product} say={say} onReady={onReady} />}
            <div className="pn-grp">
              {!f.may ? null : working || st === "checking" ? (
                <div className="pn-row static mo-again">
                  <Spin />
                  <span className="m">
                    <span className="t">Checking…</span>
                  </span>
                </div>
              ) : (
                <PnRow className="mo-again" title="Check again" value={lastChecked(conn) ? `Checked ${when(lastChecked(conn))}` : undefined} onClick={() => void f.checkAgain(kind)} chevron={false} />
              )}
            </div>
            {f.may && (
              <div className="pn-grp">
                <PnRow danger title="Disconnect" onClick={working ? undefined : () => setAskOff(true)} />
              </div>
            )}
          </>
        )}
      </div>
      <ActionSheet
        open={askOff}
        onClose={() => setAskOff(false)}
        title={`Disconnect ${p.name}?`}
        message={`Goals for ${product.name} stop using it until you connect it again.`}
        actions={[{ label: "Disconnect", danger: true, onSelect: () => void f.disconnect(kind) }]}
      />
    </div>
  );
}

function Checks({ kind, conn, f }: { kind: ConnectionKind; conn: Connection | undefined; f: CxFlows }) {
  const st = shownState(conn);
  const checks = conn?.checks ?? [];
  return (
    <section>
      <div className="pn-cap">Checks</div>
      <div className="pn-grp">
        {checks.length === 0 && (
          <div className="pn-row static">
            <Spin />
            <span className="m">
              <span className="t">Flashadz is checking it now</span>
            </span>
          </div>
        )}
        {checks.map((ck) => {
          const pend = !ck.ok && (ck.optional || (st === "warming" && ck.id === "reputation"));
          const fix = ck.ok ? null : f.fixFor(kind, ck);
          const detail = ck.detail ? hideProvider(ck.detail) : undefined;
          const inner = (
            <>
              <CheckMark kind={ck.ok ? "ok" : pend ? "pend" : "bad"} />
              <span className="m">
                <span className="t">{ck.label}</span>
                {detail && <span className="s">{detail}</span>}
              </span>
              {fix && <span className="mo-fix">Fix it</span>}
            </>
          );
          return fix ? (
            <button key={ck.id} type="button" className="pn-row wrap mo-ckr" onClick={fix}>
              {inner}
            </button>
          ) : (
            <div key={ck.id} className="pn-row static wrap mo-ckr">
              {inner}
            </div>
          );
        })}
      </div>
    </section>
  );
}

function Settings({ kind, conn, f }: { kind: ConnectionKind; conn: Connection | undefined; f: CxFlows }) {
  const choose = <span className="mo-st warn">Choose</span>;
  if (kind === "email") {
    const n = emailOf(conn).mailboxIds.length;
    return (
      <section>
        <div className="pn-cap">Settings</div>
        <div className="pn-grp">
          {conn?.secretHint && <PnRow icon={<Icon name="lock" />} iconTone="plain" title="Smartlead key" value={<span className="mo-key">{conn.secretHint}</span>} />}
          <PnRow icon={<Icon name="mail" />} iconTone="plain" title="Sending addresses" value={n ? String(n) : choose} onClick={f.may ? () => void f.openMailboxes() : undefined} chevron={f.may} />
        </div>
      </section>
    );
  }
  const m = metaOf(conn);
  const acct = m.adAccounts.find((a) => a.id === m.adAccountId);
  return (
    <section>
      <div className="pn-cap">Settings</div>
      <div className="pn-grp">
        <PnRow
          icon={<Icon name="wallet" />}
          iconTone="plain"
          title="Ad account"
          value={m.adAccountId ? (acct?.name ?? m.adAccountId) : choose}
          onClick={f.may && m.adAccounts.length > 1 ? f.openAccount : undefined}
          chevron={f.may && m.adAccounts.length > 1}
        />
        {m.signedIn && <PnRow icon={<Icon name="target" />} iconTone="plain" title="Pixel" value={m.pixelId ? (m.pixelName ?? m.pixelId) : choose} onClick={f.may ? f.openPixel : undefined} chevron={f.may} />}
        {conn?.secretHint && <PnRow icon={<Icon name="lock" />} iconTone="plain" title="Key" value={<span className="mo-key">{conn.secretHint}</span>} />}
      </div>
    </section>
  );
}

/**
 * Under cold email: each domain bought through Smartlead, a draft's Buy or Cancel, and buying more. Inside the
 * Flashsuite phone app nothing is bought (APP-MODE.md section 2): the domains show without prices, and a draft can
 * only be cancelled.
 */
function SendingDomains({ product, say, onReady }: { product: ProductRef; say: (message: string, done?: boolean) => void; onReady: () => void }) {
  const { data, err, busy, load, cancel } = useMailboxOrders(product.id, say, onReady);
  const [buying, setBuying] = useState(false);
  const [draft, setDraft] = useState<MailboxOrder | null>(null);
  const [asked, setAsked] = useState<MailboxOrder | null>(null);
  const [showCancelled, setShowCancelled] = useState(false);
  const orders = data?.orders ?? [];
  const cancelled = orders.filter((o) => o.state === "cancelled");
  const shown = orders.filter((o) => o.state !== "cancelled" || showCancelled);
  if (IN_APP && orders.length === 0 && !err) return null;

  return (
    <section>
      <div className="pn-cap">Sending domains</div>
      <div className="pn-grp">
        {shown.map((o) => {
          const w = ORDER_STATE[o.state];
          const n = mailboxCount(o);
          const sub = (
            <>
              {n} {n === 1 ? "address" : "addresses"}
              {IN_APP ? null : ` · ${priceShort(o)}`}
              {o.note && <span className="mo-onote">{o.note}</span>}
            </>
          );
          return (
            <PnRow
              key={o.id}
              className="mo-order"
              icon={<Icon name="globe" />}
              iconTone={o.state === "ready" ? "good" : "plain"}
              title={o.domains.map((d) => d.domain).join(", ")}
              sub={sub}
              wrap
              value={<span className={`pn-pill ${w.tone}`}>{w.words}</span>}
              onClick={o.state === "draft" && busy !== o.id ? () => setAsked(o) : undefined}
              chevron={o.state === "draft"}
            />
          );
        })}
        {cancelled.length > 0 && (
          <PnRow className="mo-dimr" title={showCancelled ? "Hide cancelled drafts" : `Show ${cancelled.length} cancelled ${cancelled.length === 1 ? "draft" : "drafts"}`} onClick={() => setShowCancelled((v) => !v)} chevron={false} />
        )}
        {IN_APP ? null : <PnRow className="accent" icon={<Icon name="plus" />} title="Buy sending addresses" onClick={() => setBuying(true)} chevron={false} />}
      </div>
      {err ? <p className="mo-err mo-pad">{err}</p> : IN_APP ? null : <p className="pn-note">Smartlead sets them up and charges the card on your Smartlead account.</p>}
      <ActionSheet
        open={asked !== null}
        onClose={() => setAsked(null)}
        title={asked?.domains.map((d) => d.domain).join(", ")}
        message={IN_APP ? "A draft." : "A draft. Nothing is charged until you buy."}
        actions={[
          ...(IN_APP ? [] : [{ label: "Review and buy", onSelect: () => asked && setDraft(asked) }]),
          { label: "Cancel the draft", danger: true, onSelect: () => asked && void cancel(asked) },
        ]}
      />
      {IN_APP ? null : (
        <BuyMailboxes
          productId={product.id}
          productName={product.name}
          open={buying}
          draft={draft}
          orders={orders}
          lastContact={data?.contact ?? null}
          onClose={() => {
            setBuying(false);
            setDraft(null);
          }}
          onChanged={() => void load()}
        />
      )}
    </section>
  );
}
